/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { CloseOutlined } from '@ant-design/icons'
import { App, Checkbox, Modal, InputNumberProps, Alert } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { useCallback, useState, useEffect, useMemo } from 'react'
import { useModalScroll } from 'src/hooks/useModalScroll'
import Text from 'src/components/ts-components/Text'
import useWindowSize from 'src/hooks/useWindowSize'
import Button from 'src/components/ts-components/Button'
import req from '@sportninja/common/api/request'
import { BillingScheduleItem } from '@sportninja/common/types/Registration'
import { getErrorMessage } from '@sportninja/common/utils/utils'
import { formatShortDate } from '@sportninja/common/ts-utils/date-utils'
import CurrencyInput from 'src/components/ts-components/CurrencyInput'
import { TextInput } from 'src/components/ts-components/TextInput'

const styles = css`
  .my-modal-body {
  }
  .my-modal-mask {
    backdrop-filter: blur(1px);
  }
  .my-modal-header {
    border-bottom: 1px dotted ${colors.FIERY_RED};
  }
  .my-modal-footer {
    color: ${colors.FIERY_RED};
  }
  .my-modal-content {
    border: 1px solid #333;
  }
`
const classNames = {
  body: styles['my-modal-body'],
  mask: styles['my-modal-mask'],
  header: styles['my-modal-header'],
  footer: styles['my-modal-footer'],
  content: styles['my-modal-content'],
}
const modalStyles = {
  header: {
    backgroundColor: colors.SECONDARY_300,
    paddingLeft: '16px',
    paddingRight: '16px',
  },
  body: {
    borderBottom: `1px solid ${colors.SOFT_STEEL}`,
    borderTop: `1px solid ${colors.SOFT_STEEL}`,
    backgroundColor: colors.SECONDARY_300,
  },
  content: {
    backgroundColor: colors.SECONDARY_300,
    paddingTop: '8px',
    paddingBottom: '16px',
    paddingLeft: '0',
    paddingRight: '0',
  },
  wrapper: {
    zIndex: 10002,
  },
  mask: {
    backdropFilter: 'blur(2px)',
    backgroundColor: 'rgba(6, 10, 16, 0.8)',
    zIndex: 10001,
  },
  footer: {
    paddingLeft: '16px',
    paddingRight: '16px',
  },
}

type RefundModalProps = {
  isOpen: boolean
  onClose: () => void
  billing: BillingScheduleItem | null
  refetch: (shouldRefresh: boolean) => void
  currency?: string | null | undefined
}

const RefundModal = ({
  isOpen,
  onClose,
  billing,
  refetch,
}: // currency,
RefundModalProps) => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width <= 768, [width])
  const { message } = App.useApp()
  const { openModal, closeModal } = useModalScroll()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirmChecked, setConfirmChecked] = useState(false)
  const [refundAmount, setRefundAmount] = useState(
    billing?.total?.toString() || '0'
  )
  const [refundPercentage, setRefundPercentage] = useState('100')

  // Effect to sync amount and percentage when billing changes
  useEffect(() => {
    if (billing?.total) {
      setRefundAmount(billing.total.toString())
      setRefundPercentage('100')
    }
  }, [billing?.total])

  // Handler for amount changes
  const handleAmountChange: InputNumberProps['onChange'] = useCallback(
    (value) => {
      if (!billing?.total) {
        return
      }

      const maxAmount = parseFloat(billing.total.toString())
      const newAmount = Math.min(
        parseFloat(value?.toString() || '0'),
        maxAmount
      )
      setRefundAmount(newAmount.toString())

      if (maxAmount > 0) {
        const newPercentage = ((newAmount / maxAmount) * 100).toFixed(2)
        setRefundPercentage(newPercentage)
      }
    },
    [billing?.total]
  )

  // Handler for percentage changes
  const handlePercentageChange = useCallback(
    (value: string) => {
      if (!billing?.total) {
        return
      }

      const maxAmount = parseFloat(billing.total.toString())
      const newPercentage = Math.min(parseFloat(value || '0'), 100)
      setRefundPercentage(newPercentage.toString())

      const newAmount = ((newPercentage / 100) * maxAmount).toFixed(2)
      setRefundAmount(newAmount)
    },
    [billing?.total]
  )

  const handleRefund = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)
      await req(`/billing/schedule/${billing?.uid}/refund`, {
        method: 'PUT',
        // body: {
        //   amount: refundAmount,
        // },
      })
      message.success('Payment refunded successfully')
      onClose()
      refetch(false)
    } catch (e: any) {
      const errorMessage = getErrorMessage(e)
      setError(errorMessage || 'Failed to process refund')
    } finally {
      setIsLoading(false)
    }
  }, [billing?.uid, message, onClose, refetch])

  const handleCancelRefund = useCallback(() => {
    onClose()
  }, [onClose])

  useEffect(() => {
    if (isOpen) {
      openModal()
    } else {
      closeModal()
    }
  }, [closeModal, isOpen, openModal])

  const getFooter = useCallback(() => {
    return (
      <div
        css={css`
          display: flex;
          justify-content: flex-end;
          gap: 16px;
          width: ${isMobile ? '100%' : '50%'};
          margin-left: ${isMobile ? '0' : 'auto'};
          @media (max-width: 768px) {
            width: 100%;
            flex-direction: column;
          }
        `}
      >
        <Button text='Close' onClick={handleCancelRefund} variant='secondary' />
        <Button
          text={isLoading ? 'Processing...' : 'Refund'}
          onClick={handleRefund}
          variant='primary'
          disabled={isLoading || !confirmChecked}
        />
      </div>
    )
  }, [handleCancelRefund, handleRefund, isLoading, isMobile, confirmChecked])

  const getHeader = useCallback(() => {
    return (
      <Text variant='heading3' weight='semiBold' color={colors.WHITE}>
        Refund Payment
      </Text>
    )
  }, [])

  const closeIcon = useCallback(() => {
    return (
      <CloseOutlined
        css={css`
          color: ${colors.SOLITUDE};
          font-size: 18px;
          cursor: pointer;
          padding: 12px;
          transition: all 0.2s;
          &:hover {
            color: ${colors.WHITE};
          }
        `}
      />
    )
  }, [])

  return (
    <Modal
      css={css`
        display: flex;
        flex: 1;
        background: ${colors.SECONDARY_400} !important;
        height: ${isMobile ? '100vh' : 'auto'} !important;
        margin: ${isMobile ? '0' : '24px'};
        padding: 0;
        max-width: ${isMobile ? '100%' : '40vw'};
        top: ${isMobile ? '0' : '50%'};
        .ant-modal-content {
          height: ${isMobile ? '100vh' : 'auto'} !important;
          border-radius: ${isMobile ? '0' : '8px'};
          display: flex;
          flex-direction: column;
        }
        .ant-modal-body {
          flex: 1;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          padding: 24px;
          background: ${colors.SECONDARY_300};
        }
      `}
      width={isMobile ? '100%' : '40vw'}
      closeIcon={closeIcon()}
      title={getHeader()}
      footer={getFooter()}
      open={isOpen}
      onCancel={handleCancelRefund}
      centered={!isMobile}
      maskClosable={false}
      classNames={classNames}
      styles={modalStyles}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: 16px;
          padding: 0 8px;
        `}
      >
        <Text variant='paragraphMedium' color={colors.NEUTRAL_0}>
          Choose the amount or percentage to refund:
        </Text>

        <div
          css={css`
            display: flex;
            gap: 16px;
            width: 100%;
          `}
        >
          <div
            css={css`
              flex: 1;
            `}
          >
            <CurrencyInput
              label='Refund Amount'
              value={parseFloat(refundAmount)}
              onChange={handleAmountChange}
              name='refundAmount'
              disabled={true}
            />
          </div>
          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              gap: 8px;
              align-self: stretch;
              flex: 1;
              display: none;
            `}
          >
            <TextInput
              name='refundPercentage'
              value={refundPercentage}
              setValue={handlePercentageChange}
              label='Refund Percentage (%)'
              type='number'
              min={0}
              maxLength={3}
              placeholder='Enter percentage'
              fieldErrors={[]}
              customError={{}}
            />
          </div>
        </div>

        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 16px;
          `}
        >
          <Text
            variant='captionSmall'
            color={colors.NEUTRAL_50}
            weight='semiBold'
          >
            REFUND DETAILS
          </Text>

          <div
            css={css`
              background: ${colors.SECONDARY_400};
              padding: 16px;
              border-radius: 8px;
            `}
          >
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 8px;
              `}
            >
              <div>
                <Text
                  variant='paragraphSmall'
                  color={colors.WHITE}
                  weight='bold'
                >
                  Date:
                </Text>
                <Text
                  variant='paragraphSmall'
                  color={colors.WHITE}
                  weight='regular'
                >
                  {' '}
                  {formatShortDate(billing?.due_date || '')}
                </Text>
              </div>

              <div>
                <Text
                  variant='paragraphSmall'
                  color={colors.WHITE}
                  weight='bold'
                >
                  Type:
                </Text>
                <Text
                  variant='paragraphSmall'
                  color={colors.WHITE}
                  weight='regular'
                >
                  {' '}
                  {billing?.payment_type || 'Deposit'}
                </Text>
              </div>

              <div>
                <Text
                  variant='paragraphSmall'
                  color={colors.WHITE}
                  weight='bold'
                >
                  Description:
                </Text>
                <Text
                  variant='paragraphSmall'
                  color={colors.WHITE}
                  weight='regular'
                >
                  {' '}
                  {billing?.charge?.description ||
                    'Registration Instalment Refund for Player'}
                </Text>
              </div>
            </div>
          </div>
        </div>

        <Checkbox
          checked={confirmChecked}
          onChange={(e) => setConfirmChecked(e.target.checked)}
          css={css`
            .ant-checkbox-inner {
              background-color: transparent;
              border-color: ${colors.WHITE};
            }
            .ant-checkbox-checked .ant-checkbox-inner {
              background-color: ${colors.PRIMARY};
              border-color: ${colors.PRIMARY};
            }
          `}
        >
          <Text variant='paragraphSmall' color={colors.WHITE} weight='regular'>
            I confirm that this refund is final and cannot be undone.
          </Text>
        </Checkbox>

        {error && <Alert message={error} type='error' showIcon />}
      </div>
    </Modal>
  )
}

export default RefundModal
